import localForage from 'localforage';

import { userTracking } from '@revelio/core';

import { ACCOUNT_PORTAL_ROOT } from './auth';
import { dashMetaLogout } from './auth.meta';
import { oryLoginFlow, oryLogout } from './auth.ory';

export const login = (values: { username: string; password: string }) => {
  return (
    Promise.resolve(values)
      .then(oryLoginFlow)
      // biome-ignore lint/suspicious/noExplicitAny: migrated from develop
      .then((data: any) => {
        // make the user id be the new user id rather than old backend id
        data.legacy_id = data.id;
        data['id'] = data.ory.session.identity.id;

        return { userId: data.username };
      })
  );
};

export const clearStorage = async () => {
  // Clearing browser storage first
  sessionStorage.clear();
  localStorage.clear();

  // Drop each localForage store sequentially. Performing these one-by-one avoids
  // potential IndexedDB version conflicts that can occur when multiple
  // `dropInstance` calls try to upgrade the same database in parallel.
  const storeNames = [
    // Names, not imports: this lib sits below the apps that own these stores.
    // `economic-impact-solve` holds a ~1MB solve keyed by user (see the
    // dashboard's economic-impact/solve-cache) — it has to be dropped here or
    // one user's results outlive their session on a shared machine.
    'economic-impact-solve',
    'filterStore',
    'rl_user_auth',
    'role-selection-list-store-v2',
    'selection-list-store',
  ] as const;

  for (const storeName of storeNames) {
    try {
      await localForage.dropInstance({
        name: 'rl-dashboard',
        storeName,
      });
    } catch (error) {
      // We don't want a single failure to prevent the rest of the clean-up.
      // Logging for visibility/debugging purposes.
      console.warn(`Failed to drop localForage store: ${storeName}`, error);
    }
  }
};

const logoutFallback = async () => {
  await clearStorage();
  const searchParams = `?redirect_uri=${window.location.href}`;
  window.location.assign(`${ACCOUNT_PORTAL_ROOT}/login${searchParams}`);
};

export const fullLogout = async (explicitLogout = false) => {
  userTracking.reset();

  if (window.location.pathname === '/login') {
    await clearStorage();
    return;
  }

  const isLoggingOutInAdminPagesOrExplicitLogout =
    explicitLogout ||
    window.location.pathname.includes('/admin') ||
    window.location.pathname.includes('/manage');
  const searchParams = `?redirect_uri=${window.location.href}`;

  // if anything isn't caught or takes more than 6 seconds below then just try clearing things manually and push user to login
  const logoutTimeout = setTimeout(logoutFallback, 6000);

  try {
    const dashMetaClearStorage = dashMetaLogout();
    const manualClearStorage = clearStorage();

    await Promise.all([dashMetaClearStorage, manualClearStorage]);

    await oryLogout();

    window.location.assign(
      `${ACCOUNT_PORTAL_ROOT}/login${
        isLoggingOutInAdminPagesOrExplicitLogout ? '' : searchParams
      }`
    );
  } catch {
    clearTimeout(logoutTimeout);
    logoutFallback();
  }
};
